import {
  CheckCircle,
  Clock,
  Copy,
  Edit,
  Filter,
  Info,
  Package,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../components/Pagination";
import { craClassificationColorKey } from "../constants/craClassification";
import { SHOW_DATAACT_FEATURES } from "../featureFlags";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import { hasGlobalAccess, isAdmin, Product, ProductFilters } from "../types";
import { matchesAiFeaturesFilter } from "../utils/aiFeatures";
import { matchesDataActStatusFilter } from "../utils/dataActStatusFilter";
import {
  applyListStateToSearchParams,
  filtersFromSearchParams,
  hasActiveFilters,
  PAGE_PARAM,
  pageFromSearchParams,
  ProductFilterKey,
  perPageFromSearchParams,
  productsListUrl,
  withFilter,
} from "../utils/productListParams";

export default function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth for the list view: which filters are
  // applied, which page, and how many rows. Keeping it there rather than in
  // component state is what lets the browser restore the list when the user
  // comes back from a product (issue #26), and makes a filtered list
  // shareable.
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );
  const currentPage = pageFromSearchParams(searchParams);
  const itemsPerPage = perPageFromSearchParams(searchParams);

  const updateListState = (
    next: Partial<{
      filters: ProductFilters;
      page: number;
      itemsPerPage: number;
    }>,
  ) => {
    setSearchParams(
      (prev) =>
        applyListStateToSearchParams(prev, {
          filters: next.filters ?? filtersFromSearchParams(prev),
          page: next.page ?? pageFromSearchParams(prev),
          itemsPerPage: next.itemsPerPage ?? perPageFromSearchParams(prev),
        }),
      // replace: changing a filter refines the current view rather than moving
      // somewhere new, so it should not take a history entry each time -- Back
      // from the list should leave the page, not step back through every
      // dropdown the user touched.
      { replace: true },
    );
  };

  const [showFilters, setShowFilters] = useState(false);

  // Draft text in the search box. Separate from filters.search, which is the
  // applied term: typing does not filter until Enter or Search. Seeded from
  // the URL so a restored list shows the term it is filtered by.
  const [searchTerm, setSearchTerm] = useState(() => filters.search ?? "");

  // Follow the URL if the applied term changes underneath us -- browser Back
  // or Forward while the list is still mounted. Keyed on the applied term
  // rather than the draft, so it never interrupts typing.
  useEffect(() => {
    setSearchTerm(filters.search ?? "");
  }, [filters.search]);

  // Product pending clone (shows the include-assessment chooser dialog)
  const [cloneTarget, setCloneTarget] = useState<Product | null>(null);

  // Check for 'my' parameter to filter for current user's products
  const showMyProductsOnly = searchParams.get("my") === "true";

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Fetch all products (without server-side pagination)
  const { data: productsData, isLoading } = useQuery(
    ["products", filters],
    () => apiService.getProducts({ ...filters, limit: 10000 }), // Fetch all products
    {
      keepPreviousData: true,
    },
  );

  // Fetch filter options
  const { data: filterOptions } = useQuery(
    "filterOptions",
    apiService.getFilterOptions,
  );

  // Fetch divisions for selected business area
  const { data: divisionsForBAResponse } = useQuery(
    ["divisions", filters.businessArea],
    () => apiService.getDivisions(filters.businessArea!),
    {
      enabled: !!filters.businessArea,
    },
  );

  // Delete product mutation
  const deleteMutation = useMutation(
    (productId: string) => apiService.deleteProduct(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["products"]);
        toast.success("Product deleted successfully");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to delete product");
      },
    },
  );

  // Clone product mutation
  const cloneMutation = useMutation(
    ({
      productId,
      includeAssessment,
    }: {
      productId: string;
      includeAssessment: boolean;
    }) => apiService.cloneProduct(productId, includeAssessment),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(["products"]);
        const newProduct = response.data?.data;
        setCloneTarget(null);
        toast.success("Product cloned successfully");
        if (newProduct?.id) {
          navigate(`/products/${newProduct.id}/edit`);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to clone product");
      },
    },
  );

  const handleSearch = () => {
    updateListState({
      filters: withFilter(filters, "search", searchTerm),
      page: 1,
    });
  };

  const handleFilterChange = (key: ProductFilterKey, value: string) => {
    let newFilters = withFilter(filters, key, value);

    // Reset division when business area changes
    if (key === "businessArea") {
      newFilters = withFilter(newFilters, "division", "");
    }

    updateListState({ filters: newFilters, page: 1 });
  };

  const handlePageChange = (page: number) => {
    updateListState({ page });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    updateListState({ itemsPerPage: newItemsPerPage, page: 1 });
  };

  const handleDelete = (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      deleteMutation.mutate(productId);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    updateListState({ filters: {}, page: 1 });
  };

  /**
   * Open a product, recording the list URL we came from.
   *
   * The back control on the product page reads this, so it returns to the list
   * exactly as it was rather than to a bare /products. Previously this was a
   * `window.location.href` assignment, which reloaded the whole application --
   * discarding the router's history and making Back a second full load.
   */
  const openProduct = (productId: string) => {
    navigate(`/products/${productId}`, {
      state: { from: productsListUrl(searchParams) },
    });
  };

  /**
   * URL for switching between all products and only the user's own, keeping
   * whatever filters are applied. Narrowing the scope is not a reason to throw
   * away the rest of the query.
   */
  const scopeToggleUrl = (mine: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (mine) next.set("my", "true");
    else next.delete("my");
    next.delete(PAGE_PARAM);
    return productsListUrl(next);
  };

  // Get color for score
  const getScoreColor = (score: number) => {
    if (score === 0)
      return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700";
    if (score <= 1)
      return "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-700";
    if (score <= 2)
      return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700";
    if (score <= 3)
      return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700";
    return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-700";
  };

  // Get color for CRA assessment result
  const getCRAColor = (result: string) => {
    switch (result) {
      case "in_scope_critical":
        return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700";
      case "in_scope_important":
        return "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-700";
      case "in_scope_default":
        return "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700";
      case "not_in_scope":
        return "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600";
    }
  };

  // Unwrap ApiResponse shape and apply client-side filtering and pagination
  const productList = (productsData?.data as any)?.data;
  const allProducts = productList?.products || [];

  // Client-side filtering and pagination
  const filteredAndPaginatedData = useMemo(() => {
    let filtered = [...allProducts];

    // Apply 'my products' filter if requested
    if (showMyProductsOnly && user?.id) {
      filtered = filtered.filter(
        (product: Product) => product.ownerId === user.id,
      );
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (product: Product) =>
          product.name.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower) ||
          product.owner.name.toLowerCase().includes(searchLower) ||
          product.businessArea?.toLowerCase().includes(searchLower) ||
          product.division?.toLowerCase().includes(searchLower),
      );
    }

    // Apply business area filter
    if (filters.businessArea) {
      filtered = filtered.filter(
        (product: Product) => product.businessArea === filters.businessArea,
      );
    }

    // Apply division filter
    if (filters.division) {
      filtered = filtered.filter(
        (product: Product) => product.division === filters.division,
      );
    }

    // Apply CRA status filter
    if (filters.craStatus) {
      if (filters.craStatus === "done") {
        filtered = filtered.filter(
          (product: Product) =>
            (product.craApplicability &&
              product.craApplicability !== "not_assessed") ||
            product.craClassification,
        );
      } else if (filters.craStatus === "not_done") {
        filtered = filtered.filter(
          (product: Product) =>
            (!product.craApplicability ||
              product.craApplicability === "not_assessed") &&
            !product.craClassification,
        );
      }
    }

    // Apply SDL status filter
    if (filters.sdlStatus) {
      if (filters.sdlStatus === "done") {
        filtered = filtered.filter(
          (product: Product) =>
            product.sdlMaturityScore !== null &&
            product.sdlMaturityScore !== undefined,
        );
      } else if (filters.sdlStatus === "not_done") {
        filtered = filtered.filter(
          (product: Product) =>
            product.sdlMaturityScore === null ||
            product.sdlMaturityScore === undefined,
        );
      }
    }

    // Apply approval status filter
    if (filters.status) {
      filtered = filtered.filter(
        (product: Product) => product.status === filters.status,
      );
    }

    // Apply key product filter
    if (filters.keyProduct) {
      if (filters.keyProduct === "yes") {
        filtered = filtered.filter(
          (product: Product) => product.keyProduct === true,
        );
      } else if (filters.keyProduct === "no") {
        filtered = filtered.filter((product: Product) => !product.keyProduct);
      }
    }

    // Apply AI features filter
    if (filters.aiFeatures) {
      filtered = filtered.filter((product: Product) =>
        matchesAiFeaturesFilter(product, filters.aiFeatures),
      );
    }

    // Apply CRA Compliance status filter
    if (filters.craComplianceStatus) {
      if (filters.craComplianceStatus === "submitted") {
        filtered = filtered.filter(
          (product: Product) => product.craComplianceStatus === "submitted",
        );
      } else if (filters.craComplianceStatus === "draft") {
        filtered = filtered.filter(
          (product: Product) => product.craComplianceStatus === "draft",
        );
      } else if (filters.craComplianceStatus === "not_started") {
        filtered = filtered.filter(
          (product: Product) => !product.craComplianceStatus,
        );
      }
    }

    // Apply Data Act status filter
    if (filters.dataActStatus) {
      filtered = filtered.filter((product: Product) =>
        matchesDataActStatusFilter(product, filters.dataActStatus),
      );
    }

    const total = filtered.length;
    const totalPages = Math.ceil(total / itemsPerPage);

    // Clamp the requested page to what actually exists.
    //
    // The page number now comes from the URL, so it can outrun the results --
    // a bookmarked or shared link whose filters match fewer products than they
    // did, or a hand-edited ?page=. Showing the last page is far less
    // confusing than an empty list captioned "no products found".
    const safePage = Math.min(
      Math.max(currentPage, 1),
      Math.max(totalPages, 1),
    );

    // Apply pagination
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProducts = filtered.slice(startIndex, endIndex);

    return {
      products: paginatedProducts,
      total,
      totalPages,
      safePage,
    };
  }, [
    allProducts,
    filters,
    currentPage,
    itemsPerPage,
    showMyProductsOnly,
    user?.id,
  ]);

  const { products, total, totalPages, safePage } = filteredAndPaginatedData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {showMyProductsOnly ? "My Products" : "Products"}
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            {showMyProductsOnly
              ? "Manage and view your products"
              : "Manage and view all products"}{" "}
            •
            <span
              className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                hasGlobalAccess(user)
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : isAdmin(user)
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
              }`}
            >
              {hasGlobalAccess(user)
                ? user?.roleId === "global_admin"
                  ? "Global Admin"
                  : "Global Reader"
                : isAdmin(user)
                  ? "Business Area Admin"
                  : user?.roleId === "division_admin"
                    ? "Division Admin"
                    : "Product Owner"}
            </span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {showMyProductsOnly
              ? "Products you own and manage"
              : hasGlobalAccess(user)
                ? "You can view and manage all products across the organization"
                : isAdmin(user)
                  ? "You can view and manage all products in your business area"
                  : user?.roleId === "division_admin"
                    ? "You can view all products in your business area"
                    : "You can view products in your business area and manage your own products"}
          </p>
        </div>
        <div className="flex space-x-2">
          {showMyProductsOnly && (
            <Link to={scopeToggleUrl(false)} className="btn btn-outline">
              View All Products
            </Link>
          )}
          {!showMyProductsOnly && (
            <Link to={scopeToggleUrl(true)} className="btn btn-outline">
              My Products Only
            </Link>
          )}
          <Link to="/products/create" className="btn btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 w-[90%] mx-auto">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="input pl-10"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-outline"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>

          {/* Clear Filters Button */}
          {(hasActiveFilters(filters) || searchTerm) && (
            <button
              onClick={handleClearFilters}
              className="btn btn-outline text-red-600 dark:text-red-400 border-red-600 dark:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              Clear Filters
            </button>
          )}

          {/* Search Button */}
          <button onClick={handleSearch} className="btn btn-primary">
            Search
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label
                htmlFor="businessArea"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Business Area
              </label>
              <select
                id="businessArea"
                value={filters.businessArea || ""}
                onChange={(e) =>
                  handleFilterChange("businessArea", e.target.value)
                }
                className="input"
                disabled={false}
              >
                <option value="">
                  {hasGlobalAccess(user)
                    ? "All Business Areas"
                    : "Select Business Area"}
                </option>
                {hasGlobalAccess(user)
                  ? (
                      (filterOptions?.data as any)?.data?.businessAreas || []
                    ).map((area: string) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))
                  : user?.businessArea
                    ? [
                        <option
                          key={user.businessArea}
                          value={user.businessArea}
                        >
                          {user.businessArea}
                        </option>,
                      ]
                    : []}
              </select>
            </div>

            <div>
              <label
                htmlFor="division"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Division/Business Unit
              </label>
              <select
                id="division"
                value={filters.division || ""}
                onChange={(e) => handleFilterChange("division", e.target.value)}
                className="input"
                disabled={!filters.businessArea}
              >
                <option value="">
                  {filters.businessArea
                    ? "All Divisions/Business Units"
                    : "Select Business Area First"}
                </option>
                {(filters.businessArea
                  ? (divisionsForBAResponse?.data as any)?.data || []
                  : (filterOptions?.data as any)?.data?.divisions || []
                ).map((division: string) => (
                  <option key={division} value={division}>
                    {division}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="craStatus"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                CRA Assessment
              </label>
              <select
                id="craStatus"
                value={filters.craStatus || ""}
                onChange={(e) =>
                  handleFilterChange("craStatus", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="done">CRA Done</option>
                <option value="not_done">CRA Not Done</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="sdlStatus"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                SDL Assessment
              </label>
              <select
                id="sdlStatus"
                value={filters.sdlStatus || ""}
                onChange={(e) =>
                  handleFilterChange("sdlStatus", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="done">SDL Done</option>
                <option value="not_done">SDL Not Done</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Approval Status
              </label>
              <select
                id="status"
                value={filters.status || ""}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="input"
              >
                <option value="">All Products</option>
                <option value="Approved">Approved</option>
                <option value="NotApproved">Not Approved</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="keyProduct"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Key Product
              </label>
              <select
                id="keyProduct"
                value={filters.keyProduct || ""}
                onChange={(e) =>
                  handleFilterChange("keyProduct", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="yes">Key Products</option>
                <option value="no">Non-Key Products</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="aiFeatures"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                AI Features
              </label>
              <select
                id="aiFeatures"
                value={filters.aiFeatures || ""}
                onChange={(e) =>
                  handleFilterChange("aiFeatures", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="yes">With AI Features</option>
                <option value="no">Without AI Features</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="craComplianceStatus"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                CRA Compliance
              </label>
              <select
                id="craComplianceStatus"
                value={filters.craComplianceStatus || ""}
                onChange={(e) =>
                  handleFilterChange("craComplianceStatus", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="submitted">Submitted</option>
                <option value="draft">In Progress</option>
                <option value="not_started">Not Started</option>
              </select>
            </div>

            {SHOW_DATAACT_FEATURES && (
              <div>
                <label
                  htmlFor="dataActStatus"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Data Act
                </label>
                <select
                  id="dataActStatus"
                  value={filters.dataActStatus || ""}
                  onChange={(e) =>
                    handleFilterChange("dataActStatus", e.target.value)
                  }
                  className="input"
                >
                  <option value="">All Products</option>
                  <option value="in_scope">In Scope</option>
                  <option value="out_of_scope">Out of Scope</option>
                  <option value="draft">In Progress</option>
                  <option value="not_assessed">Not Assessed</option>
                </select>
              </div>
            )}

            <div>
              <label
                htmlFor="lifecycleStatus"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Lifecycle Status
              </label>
              <select
                id="lifecycleStatus"
                value={filters.lifecycleStatus || ""}
                onChange={(e) =>
                  handleFilterChange("lifecycleStatus", e.target.value)
                }
                className="input"
              >
                <option value="">All Products</option>
                <option value="Planned">Planned</option>
                <option value="In Development">In Development</option>
                <option value="Released">Released</option>
                <option value="Retired">Retired</option>
                <option value="Obsolete">Obsolete</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Products List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Loading products...
          </p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No products found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {filters.search || filters.businessArea || filters.division
              ? "Try adjusting your filters or search terms."
              : "Get started by adding your first product."}
          </p>
          {!filters.search && !filters.businessArea && !filters.division && (
            <Link to="/products/create" className="btn btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-[90%] mx-auto">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Product
                    </th>
                    {user?.roleId !== "product_owner" && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          <div className="relative group">
                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            <div className="absolute top-full left-0 mt-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-[9999] border border-gray-300 dark:border-gray-600 normal-case">
                              This is the approval status into the Prod Atlas
                              app and nothing to do with the CRA/SDL approval.
                              <br /> <br />
                              Ask your division admin to approve your product to
                              be added in Prod Atlas.
                              <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-100 dark:border-b-gray-800"></div>
                            </div>
                          </div>
                        </div>
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Business Area
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Division/Business Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Assessments
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Key Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {products.map((product: Product) => (
                    <tr
                      key={product.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                      onClick={() => openProduct(product.id)}
                      title="Click to view product details"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 flex items-center">
                            {product.name}
                            {product.ownerId === user?.id && (
                              <span
                                className="ml-2 text-yellow-500"
                                title="My Product"
                              >
                                ⭐
                              </span>
                            )}
                          </div>
                          {product.description && (
                            <div
                              className="text-sm text-gray-500 dark:text-gray-400 max-w-xs"
                              title={product.description}
                            >
                              {truncateText(product.description, 30)}
                            </div>
                          )}
                        </div>
                      </td>
                      {user?.roleId !== "product_owner" && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              product.status === "Approved"
                                ? "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                            }`}
                          >
                            {product.status === "Approved" ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <Clock className="h-3 w-3 mr-1" />
                            )}
                            {product.status}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {product.businessArea}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {product.division}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <div className="flex items-center">
                          {product.owner.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        <div className="flex space-x-2">
                          {product.craApplicability &&
                          product.craApplicability !== "not_assessed" ? (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCRAColor(
                                product.craApplicability,
                              )}`}
                            >
                              CRA: {product.craApplicability.replace(/_/g, " ")}
                            </span>
                          ) : product.craClassification ? (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCRAColor(
                                craClassificationColorKey(
                                  product.craClassification,
                                ),
                              )}`}
                            >
                              CRA: {product.craClassification}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              CRA: -
                            </span>
                          )}
                          {product.sdlMaturityScore !== null &&
                          product.sdlMaturityScore !== undefined ? (
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getScoreColor(
                                product.sdlMaturityScore,
                              )}`}
                            >
                              SDL: {product.sdlMaturityScore.toFixed(2)}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              SDL: -
                            </span>
                          )}
                          {product.craComplianceStatus === "submitted" ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700">
                              CC: {product.craComplianceAnsweredCount ?? 0}/20
                            </span>
                          ) : product.craComplianceStatus === "draft" ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700">
                              CC: {product.craComplianceAnsweredCount ?? 0}/20
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                              CC: -
                            </span>
                          )}
                          {SHOW_DATAACT_FEATURES &&
                            (product.dataActStatus &&
                            product.dataActStatus !== "not_assessed" ? (
                              (() => {
                                const isResult =
                                  product.dataActStatus === "in_scope" ||
                                  product.dataActStatus === "out_of_scope";
                                const className = `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                  product.dataActStatus === "in_scope"
                                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700"
                                    : product.dataActStatus === "out_of_scope"
                                      ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700"
                                      : product.dataActStatus === "draft"
                                        ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700"
                                        : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700"
                                }`;
                                const label = `DA: ${product.dataActStatus.replace(/_/g, " ")}`;
                                return isResult ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/products/${product.id}/data-act-assessment`,
                                      );
                                    }}
                                    className={`${className} hover:ring-2 hover:ring-offset-1 hover:ring-primary-400 cursor-pointer transition-shadow`}
                                    title="View Data Act assessment result"
                                  >
                                    {label}
                                  </button>
                                ) : (
                                  <span className={className}>{label}</span>
                                );
                              })()
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                DA: -
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {product.keyProduct ? (
                          <svg
                            className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mx-auto"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">
                            -
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(product.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {(isAdmin(user) || product.ownerId === user?.id) && (
                            <>
                              <Link
                                to={`/products/${product.id}/edit`}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Edit className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCloneTarget(product);
                                }}
                                disabled={cloneMutation.isLoading}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
                                title="Clone product"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              {isAdmin(user) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(product.id);
                                  }}
                                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </>
      )}

      {/* Clone product dialog */}
      {cloneTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Clone product
              </h3>
            </div>
            <div className="px-6 py-4 space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <p>
                Create a copy of{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {cloneTarget.name}
                </span>
                . Fields, tags and relationships are always copied.
              </p>
              <p>
                Do you also want to copy its assessments — CRA compliance, CRA
                applicability, SDL and Data Act — as drafts?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCloneTarget(null)}
                disabled={cloneMutation.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  cloneMutation.mutate({
                    productId: cloneTarget.id,
                    includeAssessment: false,
                  })
                }
                disabled={cloneMutation.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md border border-primary-600 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
              >
                Without assessments
              </button>
              <button
                type="button"
                onClick={() =>
                  cloneMutation.mutate({
                    productId: cloneTarget.id,
                    includeAssessment: true,
                  })
                }
                disabled={cloneMutation.isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                With assessments
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
